import { useCallback, useEffect, useState } from 'react'
import { fetchDamStatus, importDam, removeDam, type BotOut, type DamStatus } from '../api'
import { mwh, price as fmtPrice, qty } from '../format'
import { BOT_KISA_TIP, useBots } from '../useBots'
import { NewBotModal } from './NewBotModal'
import type { Contract } from '../types'

/**
 * `Botlar` sekmesi — seçili kontratta çalışan botların canlı listesi.
 * Masaüstü görünümü; mobilde botların kendi sayfası var (`BotsPage`).
 *
 * Veri ve eylemler `useBots` kancasında (yoklama, kilit, tazeleme) — mobil
 * sayfayla ortak. Burada kalan tek şey bu ekranın işaretlemesi ve kaldırma
 * onayının masaüstü dili.
 *
 * Duraklatılan botun emirleri tahtada KALIR — durdurmak "yeni karar verme"
 * demek. Çekilmek isteyen botu kaldırır; kaldırma açık emirleri iptal eder,
 * kayıt geçmişi (dolumlar, olaylar) yerinde kalır.
 */

interface Props {
  contract: string | null
  contracts: Contract[]
}

export function BotsTab({ contract, contracts }: Props) {
  const [modalAcik, setModalAcik] = useState(false)
  //: Düzenlenen bot — modal `edit` modunda açılır (kimlik/tip/kontrat sabit).
  const [duzenlenen, setDuzenlenen] = useState<BotOut | null>(null)
  const { botlar, hata, mesgul, yenile, durumDegistir, kaldir } = useBots(contract)

  async function kaldirOnayla(bot: BotOut) {
    // Kaldırma açık emirleri iptal eder — yanlış tıklama telafisi yok.
    if (!window.confirm(`${bot.id} kaldırılsın mı? Açık emirleri iptal edilir.`)) return
    await kaldir(bot)
  }

  return (
    <div className="bots-tab">
      <div className="bots-toolbar">
        <span className="bots-count">
          {botlar.filter((b) => b.active && !b.expired).length}/{botlar.length} aktif bot
          {contract && <span className="muted"> · {contract}</span>}
        </span>
        <span className="bots-actions">
          {contract && <DamChip contract={contract} />}
          <button
            type="button"
            className="bots-new-btn"
            onClick={() => setModalAcik(true)}
            disabled={!contract}
          >
            + Yeni Bot Kur
          </button>
        </span>
      </div>

      {hata && <div className="bots-error">{hata}</div>}

      {botlar.length === 0 ? (
        <div className="bots-empty">
          <span>Bu kontratta çalışan bot yok.</span>
          <span className="muted">Bir şablon seçip parametreleriyle ilk botu kur.</span>
        </div>
      ) : (
        <div className="bots-rows">
          {botlar.map((bot) => (
            <div className="bot-row" key={bot.id}>
              {/* Kırmızı kontratın kapanmasıdır — duraklatmanın grisinden ayrı:
                  gri "ben durdurdum" der, kırmızı "piyasa bitti" (geri açılamaz).
                  Mavi BEST botunun görevini bitirmesi: amount doldu, teklif yok. */}
              <span
                className={`bot-dot${
                  bot.expired ? ' expired' : bot.done ? ' done' : bot.active ? ' running' : ''
                }`}
                title={
                  bot.expired ? 'Kontrat kapandı — bot artık çalışmaz'
                    : bot.done ? 'Miktar tamamlandı — bot kapalı konumda'
                      : bot.active ? 'Çalışıyor' : 'Duraklatıldı'
                }
              />
              <span className="bot-row-id" title={bot.type}>
                {bot.id}
                <span className="bot-row-tip">{BOT_KISA_TIP[bot.type] ?? bot.type}</span>
              </span>

              {/* Net pozisyon MWh (NetP sütunuyla aynı dil), tooltip lot köprüsü. */}
              <span
                className={`bot-row-net${bot.net > 0 ? ' pos' : bot.net < 0 ? ' neg' : ''}`}
                title={`Alış ${qty(bot.bought)} / Satış ${qty(bot.sold)} lot`}
              >
                {bot.net === 0 ? '–' : mwh(bot.net)}
              </span>

              {/* Tahtadaki dilimleri: yön + fiyat. */}
              <span className="bot-row-orders">
                {bot.openOrders.length === 0 ? (
                  <span className="muted">emir yok</span>
                ) : (
                  bot.openOrders.map((o) => (
                    <span
                      key={o.id}
                      className={`bot-quote ${o.side === 'BUY' ? 'buy' : 'sell'}`}
                      title={`${o.id} · ${qty(o.quantity - o.filled)} lot`}
                    >
                      {o.side === 'BUY' ? 'A' : 'S'} {fmtPrice(o.price)}
                    </span>
                  ))
                )}
              </span>

              {bot.lastError && (
                <span className="bot-row-warn" title={bot.lastError}>
                  !
                </span>
              )}

              <span className="bot-row-actions">
                <button
                  type="button"
                  className="icon-btn"
                  disabled={mesgul === bot.id}
                  title="Düzenle — kimlik ve geçmiş korunur, strateji değişir"
                  onClick={() => setDuzenlenen(bot)}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  disabled={mesgul === bot.id || bot.expired}
                  title={
                    bot.expired
                      ? 'Kontrat kapandı — bot yeniden başlatılamaz'
                      : bot.active ? 'Duraklat (emirleri tahtada kalır)' : 'Sürdür'
                  }
                  onClick={() => durumDegistir(bot)}
                >
                  {bot.active ? '⏸' : '▶'}
                </button>
                <button
                  type="button"
                  className="icon-btn danger"
                  disabled={mesgul === bot.id}
                  title="Kaldır — açık emirleri iptal edilir"
                  onClick={() => kaldirOnayla(bot)}
                >
                  🗑
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {modalAcik && contract && (
        <NewBotModal
          contract={contract}
          contracts={contracts}
          onClose={() => setModalAcik(false)}
          onCreated={yenile}
        />
      )}
      {duzenlenen && (
        <NewBotModal
          contract={duzenlenen.contract}
          contracts={contracts}
          edit={duzenlenen}
          onClose={() => setDuzenlenen(null)}
          onCreated={yenile}
        />
      )}
    </div>
  )
}

/** İşaretli MWh — GÖP değerleri API'den MWh gelir (lot köprüsü YOK). */
function gopMwh(v: number): string {
  const s = v.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return v > 0 ? `+${s}` : s
}

/**
 * GÖP Pozisyonu çipi — kontrat başına "içeri al" anahtarı (kullanıcı kararı
 * 2026-08-03). GÖP hacmi hiçbir kontrata kendiliğinden GİRMEZ: eski PnL
 * bozulurdu. Kullanıcı çipe tıklayıp "İçeri Al" derse o kontratta üç şey
 * birlikte canlanır: DAMQC() değeri, NETPC tohumu (formüller −35'ten
 * başlar) ve PnL'in PTF'den açılmış GÖP bacağı. "Çıkar" izsiz geri alır.
 */
function DamChip({ contract }: { contract: string }) {
  const [durum, setDurum] = useState<DamStatus | null>(null)
  const [acik, setAcik] = useState(false)
  const [mesgul, setMesgul] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    try {
      setDurum(await fetchDamStatus(contract))
    } catch (err) {
      setDurum(null)
      setHata(err instanceof Error ? err.message : 'GÖP durumu alınamadı')
    }
  }, [contract])

  useEffect(() => {
    setDurum(null)
    setAcik(false)
    setHata(null)
    yukle()
  }, [yukle])

  useEffect(() => {
    if (!acik) return
    const kapat = (e: KeyboardEvent) => e.key === 'Escape' && setAcik(false)
    window.addEventListener('keydown', kapat)
    return () => window.removeEventListener('keydown', kapat)
  }, [acik])

  if (durum === null && hata === null) return null      // yükleniyor — çip titremesin

  const alinan = durum?.imported ?? null
  const canli = durum?.live ?? null
  const kapali = durum != null && !durum.configured
  const deger = alinan ? alinan.mwh : (canli?.mwh ?? null)
  const veriVar = deger != null && Math.abs(deger) >= 0.05

  const etiket = kapali
    ? 'GÖP · kaynak kapalı'
    : alinan
      ? `GÖP ${gopMwh(alinan.mwh)} MWh · içeride`
      : veriVar
        ? `GÖP ${gopMwh(deger!)} MWh · dışarıda`
        : 'GÖP —'

  async function eylem() {
    setMesgul(true)
    setHata(null)
    try {
      if (alinan) await removeDam(contract)
      else await importDam(contract)
      await yukle()
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'İşlem başarısız')
    } finally {
      setMesgul(false)
    }
  }

  const alinabilir = !kapali && !alinan && veriVar && durum?.ptf != null && durum?.persisted

  return (
    <span className="dam-wrap">
      <button
        type="button"
        className={`dam-chip${alinan ? ' iceride' : veriVar ? ' disarida' : ''}`}
        aria-expanded={acik}
        title="GÖP pozisyonu — tıkla: ayrıntı ve içeri alma"
        onClick={() => setAcik((v) => !v)}
      >
        <span className="dam-dot" aria-hidden="true" />
        {etiket}
      </button>

      {acik && (
        <div className="dam-pop" role="dialog" aria-label="GÖP pozisyonu">
          <div className="dam-pop-head">
            GÖP Pozisyonu <span className="muted">· {contract}</span>
          </div>

          {kapali ? (
            <p className="dam-pop-not">
              GainSpark bağlantısı tanımlı değil (GAINSPARK_DATABASE_URL) —
              GÖP verisi okunamıyor.
            </p>
          ) : (
            <>
              {canli ? (
                <div className="dam-rows">
                  <span>GÖP Alış</span>
                  <b>{gopMwh(canli.bids)} MWh</b>
                  <span>GÖP Satış</span>
                  <b>{gopMwh(-canli.offers)} MWh</b>
                  <span>Net</span>
                  <b className={canli.mwh > 0 ? 'pos' : canli.mwh < 0 ? 'neg' : ''}>
                    {gopMwh(canli.mwh)} MWh
                  </b>
                  <span>{alinan ? 'Maliyet tabanı' : 'PTF (maliyet tabanı)'}</span>
                  <b>{fmtPrice(alinan ? alinan.ptf : durum?.ptf)}</b>
                </div>
              ) : (
                <p className="dam-pop-not">
                  Bu kontrat saati için GÖP eşleşme verisi yok ya da kaynağa ulaşılamadı.
                </p>
              )}

              {alinan && (
                <p className="dam-pop-not">
                  İçeri alındı:{' '}
                  {new Date(alinan.importedAt).toLocaleString('tr-TR', {
                    day: '2-digit', month: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}{' '}
                  · {gopMwh(alinan.mwh)} MWh @ {fmtPrice(alinan.ptf)}
                </p>
              )}

              <p className="dam-pop-uyari">
                {alinan
                  ? 'Çıkarınca NETPC, DAMQC() ve PnL etkisi bu kontrattan iz bırakmadan kalkar.'
                  : 'İçeri alınca bu kontratta NETPC ve PnL, GÖP hacmini PTF\'den ' +
                    'açılmış sayar; DAMQC() canlanır. Diğer kontratlara dokunulmaz.'}
              </p>

              {hata && <div className="dam-pop-hata">{hata}</div>}

              <button
                type="button"
                className={`dam-eylem${alinan ? ' cikar' : ''}`}
                disabled={mesgul || (!alinan && !alinabilir)}
                onClick={eylem}
              >
                {mesgul ? 'İşleniyor…' : alinan ? 'Çıkar' : 'İçeri Al'}
              </button>
            </>
          )}
        </div>
      )}
    </span>
  )
}
